import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import {
  Close,
  ExpandMore,
  VideoFile,
  Image,
  CloudUpload,
  PlayArrow,
  ImageOutlined,
  Link,
} from '@mui/icons-material';
import { MediaData } from '../../types';
import dayjs from 'dayjs';

interface MediaDetailsModalProps {
  open: boolean;
  onClose: () => void;
  media: MediaData | null;
}

const MediaDetailsModal: React.FC<MediaDetailsModalProps> = ({
  open,
  onClose,
  media,
}) => {
  if (!media) return null;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'creation': return 'primary';
      case 'starting': return 'success';
      case 'ending': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'creation': return <CloudUpload />;
      case 'starting': return <PlayArrow />;
      case 'ending': return <ImageOutlined />;
      default: return <Image />;
    }
  };

  const getFileIcon = (isVideo: boolean) => {
    return isVideo ? <VideoFile /> : <Image />;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Media Details</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Basic Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Media ID</Typography>
                  <Typography variant="body1" fontWeight="bold">{media.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                  <Chip
                    icon={getTypeIcon(media.type)}
                    label={media.type}
                    color={getTypeColor(media.type) as any}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Format</Typography>
                  <Chip
                    icon={getFileIcon(media.isVideo)}
                    label={media.isVideo ? 'Video' : 'Image'}
                    color={media.isVideo ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                  <Typography variant="body1">
                    {dayjs(media.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">File Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">File URL</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        flex: 1,
                        wordBreak: 'break-all'
                      }}
                    >
                      {media.fileUrl}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Link />}
                      onClick={() => copyToClipboard(media.fileUrl)}
                    >
                      Copy
                    </Button>
                  </Box>
                </Box>
                {media.updatedAt && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                    <Typography variant="body1">
                      {dayjs(media.updatedAt).format('MMM DD, YYYY HH:mm:ss')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Preview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Card>
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    {media.isVideo ? (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Video Preview
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Video files cannot be previewed in this modal
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            startIcon={<PlayArrow />}
                            onClick={() => window.open(media.fileUrl, '_blank')}
                          >
                            Open Video
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Image Preview
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <img
                            src={media.fileUrl}
                            alt="Media preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: 400,
                              objectFit: 'contain',
                              borderRadius: 8,
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            startIcon={<Image />}
                            onClick={() => window.open(media.fileUrl, '_blank')}
                          >
                            Open Image
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaDetailsModal;
